"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="hero" data-nav="light" className="relative w-full border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
          {/* Left Column: Primary Copy & CTAs */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="lg:col-span-7">
            {/* Eyebrow */}
            <div className="mb-4 inline-flex items-center gap-2 border border-border bg-muted px-3 py-1 font-mono text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              <span className="inline-block size-1.5 bg-primary" />
              SETUP • SPACE • POWER • OPERATION
            </div>

            {/* H1 Heading */}
            <h1 className="font-serif text-4xl font-bold tracking-wide uppercase sm:text-5xl md:text-6xl">
              Mechanical Bull Setup &amp; Safety Guide
            </h1>

            {/* Body Copy */}
            <div className="mt-6 space-y-4 font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
              <p>
                A successful mechanical bull setup starts before the equipment arrives. You need enough operating space, suitable access, the correct power for the exact machine and a clear understanding of how the bull, controller, inflatable safety arena and blower work together.
              </p>
              <p>
                Because mechanical bulls can differ by model and configuration, use this guide for planning and comparison, then confirm the final requirements from the documentation supplied for the specific machine you intend to operate.
              </p>
            </div>

            {/* Action CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild className="h-12 border border-primary bg-primary px-7 font-serif text-base font-semibold tracking-wider text-primary-foreground uppercase hover:bg-primary/90">
                <Link to="/NewMechanicalBullsPage">
                  View Mechanical Bulls
                  <ArrowUpRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-12 border-foreground bg-transparent px-7 font-serif text-base font-semibold tracking-wider text-foreground uppercase hover:bg-foreground hover:text-background">
                <Link to="/MechanicalBullBuyingGuidePage">
                  Read Buying Guide
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Visual Setup Planning Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="flex flex-col justify-between border border-border bg-card p-6 lg:col-span-5 sm:p-8">
            <div>
              <div className="flex items-center justify-between border-b border-border pb-4">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-5 text-primary" />
                  <span className="font-serif text-lg font-bold tracking-wider text-foreground uppercase">
                    Setup Considerations
                  </span>
                </div>
                <span className="font-mono text-xs text-muted-foreground uppercase">
                  Planning Overview
                </span>
              </div>

              <div className="mt-6 space-y-4">
                <div className="border-l-2 border-border pl-4 transition-colors hover:border-primary">
                  <div className="font-mono text-xs font-semibold tracking-wider text-primary uppercase">
                    01 / Operating Space
                  </div>
                  <p className="mt-1 font-sans text-xs leading-relaxed text-muted-foreground">
                    Adequate floor perimeter and vertical ceiling clearance around the full boundary of the inflatable cushion.
                  </p>
                </div>

                <div className="border-l-2 border-border pl-4 transition-colors hover:border-primary">
                  <div className="font-mono text-xs font-semibold tracking-wider text-primary uppercase">
                    02 / Electrical Drop
                  </div>
                  <p className="mt-1 font-sans text-xs leading-relaxed text-muted-foreground">
                    Dedicated, stable power circuit verified against the manufacturer documentation for your specific machine model.
                  </p>
                </div>

                <div className="border-l-2 border-border pl-4 transition-colors hover:border-primary">
                  <div className="font-mono text-xs font-semibold tracking-wider text-primary uppercase">
                    03 / Logistics &amp; Access
                  </div>
                  <p className="mt-1 font-sans text-xs leading-relaxed text-muted-foreground">
                    Clear transit pathways through doorways, loading ramps, and gates suitable for transporting heavy base equipment.
                  </p>
                </div>

                <div className="border-l-2 border-border pl-4 transition-colors hover:border-primary">
                  <div className="font-mono text-xs font-semibold tracking-wider text-primary uppercase">
                    04 / Integrated Arena
                  </div>
                  <p className="mt-1 font-sans text-xs leading-relaxed text-muted-foreground">
                    Proper placement of the air cushion, continuous blower, and operator console with an unobstructed view of riders.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 border-t border-border pt-5">
              <div className="flex items-center justify-between font-mono text-xs text-muted-foreground">
                <span>VERIFY SPECIFIC OEM MANUAL</span>
                <Link to="/ContactPage" className="font-medium text-primary hover:underline">
                  Ask Our Team &rarr;
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}